import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, ChevronRight, ListOrdered, Loader2, X } from 'lucide-react';
import { escolherVizinhas, haversineKm } from '../../src/lib/routeOptimizer';

/**
 * "Visitar: N" da rota de cobrança.
 *
 * Era um <select> nativo com "N vizinhas, a partir da 1ª" repetido até o fim da
 * rota — 36 linhas iguais para achar um número, sem dizer QUEM entrava (pedido
 * de 28/09/2026). Agora é uma grade de números: tocar no 10 acende do 1 ao 10 e
 * mostra, antes de confirmar, quais paradas vão e quanto elas se espalham. Sobe
 * de baixo no celular (o polegar alcança) e vira um cartão no computador.
 */

export interface ParadaParaEscolher {
    id: string;
    posicao: number;
    nome: string;
    /** Vale o pino aproximado: errar a porta por um quarteirão não muda quem é vizinho de quem. */
    ponto: { lat: number; lng: number } | null;
}

interface Props {
    /** As pendentes da vista, na ordem da fila. */
    paradas: ParadaParaEscolher[];
    /** O recorte em uso; `null` é "todas". */
    escolhidas: string[] | null;
    /** Das escolhidas, quantas ainda faltam visitar. */
    faltam: number;
    ocupado: boolean;
    onAbrir?: () => void;
    /** `null` volta para todas. */
    onEscolher: (n: number | null) => void;
}

/** Quantas paradas o caminho mostra antes do "+N". */
const MAX_CHIPS = 10;

const primeiroNome = (nome: string) => {
    const n = nome.trim().split(/\s+/)[0] || nome;
    return /^UC$/i.test(n) ? nome : n.charAt(0) + n.slice(1).toLowerCase();
};

const km = (v: number) => (v < 10 ? v.toFixed(1).replace('.', ',') : String(Math.round(v)));

const EscolherQuantasVisitar: React.FC<Props> = ({ paradas, escolhidas, faltam, ocupado, onAbrir, onEscolher }) => {
    const [aberto, setAberto] = useState(false);
    const emUso = escolhidas ? escolhidas.length : null;
    const [sel, setSel] = useState<number | null>(emUso);
    /** O número sob o mouse: pré-visualiza sem escolher. No toque não existe. */
    const [previa, setPrevia] = useState<number | null>(null);
    const gatilhoRef = useRef<HTMLButtonElement>(null);
    const dialogoRef = useRef<HTMLDivElement>(null);
    const idTitulo = useId();

    // Depois de visitar algumas, o recorte em uso pode ser maior do que o que
    // sobrou na fila: a grade vai até o maior dos dois (como o select fazia).
    const total = Math.max(paradas.length, emUso ?? 0);
    const mostrado = previa ?? sel;

    const abrir = () => {
        setSel(emUso);
        setPrevia(null);
        setAberto(true);
        onAbrir?.();
    };
    const fechar = () => {
        setAberto(false);
        gatilhoRef.current?.focus();
    };
    const confirmar = () => {
        setAberto(false);
        if (sel !== emUso) onEscolher(sel);
    };

    useEffect(() => {
        if (!aberto) return;
        const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape') fechar(); };
        document.addEventListener('keydown', aoTeclar);
        // Sem isto, arrastar a grade no celular rolava a lista de paradas por baixo.
        const overflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        requestAnimationFrame(() => dialogoRef.current
            ?.querySelector<HTMLElement>('[aria-pressed="true"]')?.focus());
        return () => {
            document.removeEventListener('keydown', aoTeclar);
            document.body.style.overflow = overflow;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aberto]);

    /** Quem entra com o número mostrado, na ordem do caminho — a mesma conta de quem confirma. */
    const caminho = useMemo(() => {
        if (mostrado == null) return null;
        const porId = new Map(paradas.map(p => [p.id, p]));
        return escolherVizinhas(paradas, mostrado)
            .map(id => porId.get(id))
            .filter((p): p is ParadaParaEscolher => !!p);
    }, [paradas, mostrado]);

    /** A linha reta de uma à seguinte, somada: não é o que o carro anda, mas denuncia o recorte que atravessa o estado. */
    const espalhamento = useMemo(() => {
        const pts = (caminho ?? []).map(p => p.ponto).filter((p): p is { lat: number; lng: number } => !!p);
        if (pts.length < 2) return null;
        let d = 0;
        for (let i = 1; i < pts.length; i++) d += haversineKm(pts[i - 1], pts[i]);
        return d;
    }, [caminho]);

    const partida = caminho?.[0] ?? paradas[0];
    const semMudanca = sel === emUso;
    const rotuloCta = semMudanca
        ? 'Manter como está'
        : sel == null ? `Ver todas (${paradas.length})`
            : sel === 1 ? 'Visitar só a próxima'
                : `Visitar estas ${sel}`;

    const ativo = emUso != null;

    return (
        <>
            <button
                ref={gatilhoRef}
                type="button"
                onClick={abrir}
                disabled={ocupado}
                aria-haspopup="dialog"
                aria-expanded={aberto}
                className={`flex items-center gap-1.5 pl-3 pr-2 py-2 rounded-xl border text-xs font-bold transition-colors disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5F6C37]/40 ${ativo
                    ? 'bg-[#5F6C37] border-[#5F6C37] text-white'
                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-[#5F6C37]'}`}
            >
                {ocupado ? <Loader2 size={14} className="animate-spin" /> : <ListOrdered size={14} />}
                Visitar
                <span className={`px-1.5 py-0.5 rounded-md text-[11px] font-black tabular-nums leading-none ${ativo
                    ? 'bg-white/20 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
                    {ativo ? `${emUso} · faltam ${faltam}` : `todas · ${paradas.length}`}
                </span>
                <ChevronDown size={13} className="opacity-70" />
            </button>

            {aberto && createPortal(
                <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center sm:p-4">
                    <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-[2px]" onClick={fechar} aria-hidden="true" />

                    <div
                        ref={dialogoRef}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={idTitulo}
                        className="relative w-full sm:max-w-sm max-h-[88vh] flex flex-col bg-white dark:bg-gray-900 rounded-t-3xl sm:rounded-3xl shadow-2xl ring-1 ring-black/5 dark:ring-white/10 animate-slide-up motion-reduce:animate-none"
                    >
                        <div className="sm:hidden mx-auto mt-2.5 h-1 w-10 rounded-full bg-gray-300 dark:bg-gray-700" />

                        <header className="px-5 pt-3 sm:pt-5 pb-3 flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p className="text-[10px] font-black uppercase tracking-[0.14em] text-[#5F6C37] dark:text-[#b8c77f]">
                                    Visitar hoje
                                </p>
                                <h2 id={idTitulo} className="text-lg font-black text-gray-800 dark:text-gray-100 leading-tight">
                                    Quantas paradas?
                                </h2>
                                {partida && (
                                    <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
                                        Entram as mais perto umas das outras, saindo da{' '}
                                        <strong className="text-gray-700 dark:text-gray-200">#{partida.posicao} {primeiroNome(partida.nome)}</strong>.
                                    </p>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={fechar}
                                aria-label="Fechar"
                                className="shrink-0 p-2 -mr-2 -mt-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            >
                                <X size={18} />
                            </button>
                        </header>

                        <div className="px-5 pb-4 overflow-y-auto overscroll-contain">
                            <button
                                type="button"
                                onClick={() => setSel(null)}
                                onPointerEnter={() => setPrevia(null)}
                                aria-pressed={sel == null}
                                className={`w-full flex items-center gap-3 p-2.5 rounded-2xl border-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5F6C37]/40 ${sel == null
                                    ? 'border-[#5F6C37] bg-[#5F6C37]/[0.06] dark:bg-[#5F6C37]/20'
                                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}`}
                            >
                                <span className={`w-10 h-10 shrink-0 rounded-xl grid place-items-center text-sm font-black tabular-nums ${sel == null
                                    ? 'bg-[#5F6C37] text-white'
                                    : 'bg-gray-100 dark:bg-gray-800 text-gray-500'}`}>
                                    {paradas.length}
                                </span>
                                <span className="flex-1 min-w-0">
                                    <span className="flex items-center gap-1.5 text-sm font-black text-gray-800 dark:text-gray-100">
                                        Todas
                                        {!ativo && <EmUso />}
                                    </span>
                                    <span className="block text-[11px] text-gray-500 dark:text-gray-400">Na ordem da fila, sem recorte</span>
                                </span>
                                {sel == null && <Check size={18} className="shrink-0 text-[#5F6C37] dark:text-[#b8c77f]" />}
                            </button>

                            <div className="my-3.5 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.14em] text-gray-400">
                                <span className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                                ou só as vizinhas
                                <span className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                            </div>

                            <div
                                className="grid grid-cols-6 gap-1.5"
                                role="group"
                                aria-label="Quantas paradas vizinhas"
                                onPointerLeave={() => setPrevia(null)}
                            >
                                {Array.from({ length: total }, (_, i) => i + 1).map(n => {
                                    const fim = mostrado === n;
                                    const dentro = mostrado != null && n < mostrado;
                                    return (
                                        <button
                                            key={n}
                                            type="button"
                                            onClick={() => setSel(n)}
                                            onPointerEnter={e => { if (e.pointerType === 'mouse') setPrevia(n); }}
                                            aria-pressed={sel === n}
                                            aria-label={n === 1 ? 'Só a próxima' : `${n} paradas vizinhas`}
                                            // Entrada em cascata: a grade "enche" da 1 para a última.
                                            style={{ animationDelay: `${Math.min(n, 36) * 10}ms`, animationDuration: '260ms', animationFillMode: 'both' }}
                                            className={`relative h-11 rounded-xl border text-sm font-black tabular-nums transition-[background-color,border-color,color,transform] duration-150 animate-fade-in motion-reduce:animate-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#5F6C37]/50 dark:focus-visible:ring-offset-gray-900 ${fim
                                                ? 'bg-[#5F6C37] border-[#5F6C37] text-white scale-[1.06] shadow-[0_6px_14px_-6px_rgba(95,108,55,0.7)]'
                                                : dentro
                                                    ? 'bg-[#5F6C37]/10 border-[#5F6C37]/25 text-[#5F6C37] dark:bg-[#5F6C37]/25 dark:border-[#5F6C37]/40 dark:text-[#c9d59a]'
                                                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300'}`}
                                        >
                                            {n}
                                            {emUso === n && (
                                                <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-[#F6A937]" aria-hidden="true" />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                            {ativo && (
                                <p className="mt-2 flex items-center gap-1.5 text-[10px] text-gray-400">
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#F6A937]" /> o recorte que está valendo
                                </p>
                            )}
                        </div>

                        <footer className="px-5 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] sm:pb-5 border-t border-gray-100 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-950/40 sm:rounded-b-3xl space-y-3">
                            <div className="flex items-end gap-3 min-h-[2.75rem]" aria-live="polite">
                                <span className="text-[2.5rem] leading-[0.85] font-black tabular-nums tracking-tight text-[#5F6C37] dark:text-[#b8c77f]">
                                    {mostrado ?? paradas.length}
                                </span>
                                <div className="min-w-0 pb-0.5">
                                    <p className="text-sm font-black text-gray-800 dark:text-gray-100 leading-tight">
                                        {mostrado == null ? 'todas as paradas' : mostrado === 1 ? 'só a próxima' : 'paradas vizinhas'}
                                    </p>
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight truncate">
                                        {mostrado == null
                                            ? 'na ordem em que estão na fila'
                                            : espalhamento != null
                                                ? <span title="Somando a linha reta de uma parada à seguinte. Na rua é mais.">~{km(espalhamento)} km em linha reta, de parada em parada</span>
                                                : partida && `#${partida.posicao} ${primeiroNome(partida.nome)}`}
                                    </p>
                                </div>
                            </div>

                            {caminho && caminho.length > 1 && (
                                <ol className="flex flex-wrap items-center gap-y-1 text-[11px] font-bold tabular-nums" aria-label="Paradas que entram, na ordem do caminho">
                                    {caminho.slice(0, MAX_CHIPS).map((p, i) => (
                                        <li key={p.id} className="flex items-center" title={p.ponto ? p.nome : `${p.nome} — sem pino, entra no fim`}>
                                            {i > 0 && <ChevronRight size={11} className="mx-px text-gray-300 dark:text-gray-600" aria-hidden="true" />}
                                            <span className={`px-1.5 py-0.5 rounded-md border ${p.ponto
                                                ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200'
                                                : 'border-dashed border-gray-300 dark:border-gray-600 text-gray-400'}`}>
                                                #{p.posicao}
                                            </span>
                                        </li>
                                    ))}
                                    {caminho.length > MAX_CHIPS && (
                                        <li className="ml-1.5 text-gray-400">+{caminho.length - MAX_CHIPS}</li>
                                    )}
                                </ol>
                            )}

                            <button
                                type="button"
                                onClick={confirmar}
                                className={`w-full py-3 rounded-2xl text-sm font-black transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#5F6C37]/50 dark:focus-visible:ring-offset-gray-900 ${semMudanca
                                    ? 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                                    : 'bg-[#5F6C37] hover:bg-[#4a5429] text-white shadow-[0_8px_20px_-10px_rgba(95,108,55,0.9)]'}`}
                            >
                                {rotuloCta}
                            </button>
                        </footer>
                    </div>
                </div>,
                document.body,
            )}
        </>
    );
};

const EmUso: React.FC = () => (
    <span className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider leading-none bg-[#F6A937]/15 text-[#a86a0b] dark:text-[#F6A937]">
        em uso
    </span>
);

export default EscolherQuantasVisitar;
